import { DtaAccountField, fiduciarieMessages, FISCAL_CODE_MAX_LENGTH, VAT_CODE_MAX_LENGTH } from 'azshared';
import type { DtaListedAccount } from 'azshared';
import { MessageDescriptor } from 'react-intl';
import { AppInputTextProps } from '../../../components/input-text/app-input-text';

export interface AccountField {
  key: DtaAccountField;
  label: MessageDescriptor;
  placeholder: MessageDescriptor;
  inputMode?: AppInputTextProps['inputMode'];
  maxLength?: number;
  uppercase?: boolean;
  // The trust company has either a codice fiscale or a partita IVA: filling one disables the other.
  exclusiveWith?: DtaAccountField;
}

export interface AccountFieldGroup {
  key: string;
  title: MessageDescriptor;
  fields: AccountField[];
}

// One input for each field of DtaAccountRequest, keyed by the path the server reports in campiNonValidi.
// Grouped only for display: the fiduciante and the legal person (trust company) it represents.
// Shared by the create page and the read-only detail page.
export const accountFieldGroups: AccountFieldGroup[] = [
  {
    key: 'fiduciante',
    title: fiduciarieMessages.fiducianteSectionTitle,
    fields: [
      { key: DtaAccountField.ActingPersonName, label: fiduciarieMessages.nameInputLabel, placeholder: fiduciarieMessages.nameInputPlaceholder },
      {
        key: DtaAccountField.ActingPersonSurname,
        label: fiduciarieMessages.surnameInputLabel,
        placeholder: fiduciarieMessages.surnameInputPlaceholder
      },
      {
        key: DtaAccountField.ActingPersonCodiceFiscale,
        label: fiduciarieMessages.actingPersonCodiceFiscaleInputLabel,
        placeholder: fiduciarieMessages.actingPersonCodiceFiscaleInputPlaceholder,
        maxLength: FISCAL_CODE_MAX_LENGTH,
        uppercase: true
      },
      { key: DtaAccountField.ContactPhone, label: fiduciarieMessages.cellulareInputLabel, placeholder: fiduciarieMessages.cellulareInputPlaceholder },
      { key: DtaAccountField.ContactEmail, label: fiduciarieMessages.emailInputLabel, placeholder: fiduciarieMessages.emailInputPlaceholder }
    ]
  },
  {
    key: 'legal-person',
    title: fiduciarieMessages.legalPersonSectionTitle,
    fields: [
      {
        key: DtaAccountField.TrustCompanyName,
        label: fiduciarieMessages.ragioneSocialeInputLabel,
        placeholder: fiduciarieMessages.ragioneSocialeInputPlaceholder
      },
      {
        key: DtaAccountField.TrustCompanyCode,
        label: fiduciarieMessages.codiceClienteRappresentanteInputLabel,
        placeholder: fiduciarieMessages.codiceClienteRappresentanteInputPlaceholder
      },
      { key: DtaAccountField.TrustCompanyAgent, label: fiduciarieMessages.agentInputLabel, placeholder: fiduciarieMessages.agentInputPlaceholder },
      {
        key: DtaAccountField.TrustCompanyCodiceFiscale,
        label: fiduciarieMessages.codiceFiscaleInputLabel,
        placeholder: fiduciarieMessages.codiceFiscaleInputPlaceholder,
        maxLength: FISCAL_CODE_MAX_LENGTH,
        uppercase: true,
        exclusiveWith: DtaAccountField.TrustCompanyPartitaIva
      },
      {
        key: DtaAccountField.TrustCompanyPartitaIva,
        label: fiduciarieMessages.partitaIvaInputLabel,
        placeholder: fiduciarieMessages.partitaIvaInputPlaceholder,
        inputMode: 'numeric',
        maxLength: VAT_CODE_MAX_LENGTH,
        exclusiveWith: DtaAccountField.TrustCompanyCodiceFiscale
      },
      {
        key: DtaAccountField.AddressBook,
        label: fiduciarieMessages.addressBookInputLabel,
        placeholder: fiduciarieMessages.addressBookInputPlaceholder
      }
    ]
  }
];

export const accountFieldValue = (account: DtaListedAccount, key: DtaAccountField): string | undefined => {
  switch (key) {
    case DtaAccountField.ActingPersonName:
      return account.actingPerson.name;
    case DtaAccountField.ActingPersonSurname:
      return account.actingPerson.surname;
    case DtaAccountField.ActingPersonCodiceFiscale:
      return account.actingPerson.codiceFiscale;
    case DtaAccountField.TrustCompanyName:
      return account.trustCompany.name;
    case DtaAccountField.TrustCompanyCode:
      return account.trustCompany.code;
    case DtaAccountField.TrustCompanyAgent:
      return account.trustCompany.agent;
    case DtaAccountField.TrustCompanyCodiceFiscale:
      return account.trustCompany.codiceFiscale;
    case DtaAccountField.TrustCompanyPartitaIva:
      return account.trustCompany.partitaIva;
    case DtaAccountField.ContactPhone:
      return account.contact.phone;
    case DtaAccountField.ContactEmail:
      return account.contact.email;
    case DtaAccountField.AddressBook:
      return account.addressBook;
  }
};
